/** User-facing labels and messages (hide DMTA/BMTA / internal alert_kind names). */

export const MISSING_TRIGGER_WEBHOOK_MESSAGE =
  'No webhook to trigger this alert has been created yet. Please edit the alert and generate a URL.';

export const MISSING_USER_INCOMING_WEBHOOK_MESSAGE =
  'No incoming webhook URL has been created yet. Open Settings and generate a URL.';

export function alertKindDisplayLabel(kind: string): string {
  const k = (kind || '').trim().toLowerCase();
  if (k === 'bidirectional_multi') return 'Two-Way Trade';
  if (k === 'directional_multi' || k === 'single') return 'Directional Trade';
  if (k === 'legacy_multi') return 'Multi (legacy)';
  if (!k) return 'Alert';
  return k
    .replace(/bidirectional_multi/gi, 'Two-Way Trade')
    .replace(/directional_multi/gi, 'Directional Trade')
    .replace(/_/g, ' ')
    .replace(/\b(bmta|dmta)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function slotConsensusDisplayLabel(
  consensus: 'all_up' | 'all_down' | 'all_active' | null | undefined
): string | null {
  if (consensus === 'all_up') return 'All indicators UP (trade armed)';
  if (consensus === 'all_down') return 'All indicators DOWN (trade armed)';
  if (consensus === 'all_active') return 'All indicators active';
  return null;
}

const EXACT_MESSAGE_MAP: Record<string, string> = {
  'No webhook group fired; consensus unchanged.':
    'Indicators not all aligned yet. Waiting for full alignment.',
  'Indicators not all aligned yet. Waiting for full alignment.':
    'Indicators not all aligned yet. Waiting for full alignment.',
  'No matching alert configuration found':
    'No alert matches this signal (ticker, market condition, and secret word). Check your alert setup.',
  'Two-Way Trade updates one indicator slot per trigger.':
    'Two-Way Trade updates one indicator slot per trigger.',
  'Two-Way Trade slot triggers must specify up or down (use the Up or Down trigger URL, or direction in the JSON payload).':
    'Two-Way Trade slot triggers must specify up or down (use the Up or Down trigger URL, or direction in the JSON payload).',
};

function replaceLegacyTerms(msg: string): string {
  return msg
    .replace(/\bBi-directional multi-trigger\b/gi, 'Two-Way Trade')
    .replace(/\bDirectional multi-trigger\b/gi, 'Directional Trade')
    .replace(/\bBMTA\b/g, 'Two-Way Trade')
    .replace(/\bDMTA\b/g, 'Directional Trade')
    .replace(/\bbidirectional multi\b/gi, 'Two-Way Trade')
    .replace(/\bdirectional multi\b/gi, 'Directional Trade');
}

/** Map stored execution log / API error text to dashboard-friendly copy. */
export function formatRecentAlertMessage(
  raw?: string | null,
  status?: string
): string | null {
  const msg = (raw ?? '').trim();
  if (msg) {
    if (EXACT_MESSAGE_MAP[msg]) return EXACT_MESSAGE_MAP[msg];

    if (/No trigger token for slot/i.test(msg) || /slot\/\d+\/generate-token/i.test(msg)) {
      return MISSING_TRIGGER_WEBHOOK_MESSAGE;
    }
    if (/No trigger token for this alert/i.test(msg)) {
      return MISSING_TRIGGER_WEBHOOK_MESSAGE;
    }
    if (/No trigger token set/i.test(msg)) {
      return MISSING_USER_INCOMING_WEBHOOK_MESSAGE;
    }
    if (/Invalid or expired trigger token/i.test(msg)) {
      return 'The trigger URL or token is invalid or expired. Edit the alert and generate a new URL.';
    }

    if (/No matching alert configuration found/i.test(msg)) {
      return EXACT_MESSAGE_MAP['No matching alert configuration found'];
    }
    if (/No alert configuration found for/i.test(msg) || /^No alert found for/i.test(msg)) {
      return 'No alert found for that ticker and market condition. Create or activate a matching alert.';
    }
    if (/Alert configuration \d+ not found or inactive/i.test(msg)) {
      return 'That alert is missing or turned off.';
    }

    if (/No exit alert is linked to indicator slot/i.test(msg)) {
      return replaceLegacyTerms(msg);
    }
    if (/Indicator slot \d+ does not use signal source/i.test(msg)) {
      return replaceLegacyTerms(msg);
    }
    if (/Several exit alerts match this signal/i.test(msg)) {
      return replaceLegacyTerms(msg);
    }
    if (/This alert has more than one slot using the same signal source/i.test(msg)) {
      return replaceLegacyTerms(msg);
    }

    if (/No BMTA consensus transition/i.test(msg)) {
      return 'Indicators not all aligned yet. Waiting for full alignment.';
    }
    if (/No active webhooks tagged for BMTA group/i.test(msg)) {
      return 'No execution webhook configured for that Two-Way Trade path. Add URLs under Alerts → Manage Webhooks (Up, Down, or Break).';
    }
    if (/No active webhooks tagged for DMTA group/i.test(msg)) {
      return 'No execution webhook configured for that Directional Trade path. Add URLs under Alerts → Manage Webhooks (Enter or Exit).';
    }
    if (/No execution webhook configured for Two-Way Trade group/i.test(msg)) {
      return replaceLegacyTerms(msg);
    }
    if (/No execution webhook configured for Directional Trade group/i.test(msg)) {
      return replaceLegacyTerms(msg);
    }
    if (/Invalid BMTA execution group/i.test(msg)) {
      return 'Invalid Two-Way Trade execution group. Use Up, Down, or Break in Manage Webhooks.';
    }
    if (/Invalid DMTA execution group/i.test(msg)) {
      return 'Invalid Directional Trade execution group. Use Enter or Exit in Manage Webhooks.';
    }
    if (/Invalid Two-Way Trade execution group/i.test(msg)) {
      return 'Invalid Two-Way Trade execution group. Use Up, Down, or Break in Manage Webhooks.';
    }
    if (/Invalid Directional Trade execution group/i.test(msg)) {
      return 'Invalid Directional Trade execution group. Use Enter or Exit in Manage Webhooks.';
    }
    if (/BMTA activation targets exactly one slot/i.test(msg)) {
      return 'Two-Way Trade updates one indicator slot per trigger. Send separate triggers for each slot.';
    }
    if (/Bi-directional multi-trigger activation requires direction/i.test(msg)) {
      return 'Two-Way Trade slot triggers must specify up or down (use the Up or Down trigger URL, or direction in the JSON payload).';
    }

    if (/No webhook found for/i.test(msg) || /No webhook mapping found for/i.test(msg)) {
      return 'No execution webhook URL is configured for that ticker and order type.';
    }
    if (/Webhook request timed out/i.test(msg)) {
      return 'The execution webhook did not respond in time.';
    }
    if (/All \d+ retry attempts failed/i.test(msg)) {
      return 'The execution webhook failed after several tries. Check the webhook URL and try again.';
    }
    if (/HTTP 429|Rate limit exceeded/i.test(msg)) {
      return 'The execution webhook is rate limited. Wait and try again.';
    }
    if (/Webhook \d+: HTTP \d+/i.test(msg) || /^HTTP \d+:/i.test(msg)) {
      return 'The execution webhook returned an error response. Check the URL and receiver logs.';
    }
    if (/^Webhook \d+:/i.test(msg)) {
      return 'The execution webhook failed. Check the URL and receiver logs.';
    }

    return replaceLegacyTerms(msg);
  }

  if (status === 'pending') {
    return 'Indicators not all aligned yet. Waiting for full alignment.';
  }
  if (status === 'error' && !msg) {
    return 'Execution failed. Open Settings → Incoming webhook activity for the full request log.';
  }
  return null;
}

/** Dashboard setup notices (incoming trigger URL checks). */
export function formatDashboardNoticeMessage(raw?: string | null): string {
  return formatRecentAlertMessage(raw) ?? MISSING_TRIGGER_WEBHOOK_MESSAGE;
}
